import React, { useEffect, useState } from "react";
import { FaKey, FaMask, FaUser } from "react-icons/fa";
import { RxReset } from "react-icons/rx";
import { TiCancel } from "react-icons/ti";

import Button from "../../../components/Button";
import { useToaster } from "../../../components/toaster";
import { RokaApiError } from "../../../errors/RokaApiError";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";
import { useAuth } from "../../../hooks/utility-hoks/useAuth";
import { useUserImpersonation } from "../../../hooks/utility-hoks/useUserImpersonation";
import { User } from "../../../types/rokaTypes";

interface UserFormState {
  isAdmin: boolean;
  site: string;
  isDirty: boolean;
}

interface UserAdminFormProps {
  user: User;
  onChange: (user: User) => void;
}

const UserAdminForm = ({ user, onChange }: UserAdminFormProps) => {
  const { updateUser, updateUserPassword } = useAdminApi();
  const { showToaster } = useToaster();
  const { startImpersonating, stopImpersonating } = useUserImpersonation();
  const { user: currentUser, isImpersonating } = useAuth();

  const [userFormState, setUserFormState] = useState<UserFormState>({
    isAdmin: user.isAdmin,
    site: user.site,
    isDirty: false,
  });
  const [newPassword, setNewPassword] = useState<string>("");

  const isCurrentlyImpersonated =
    isImpersonating && currentUser?.username === user.username;

  // Helper component for section headers
  const SectionHeader = ({
    icon: Icon,
    title,
  }: {
    icon: React.ComponentType;
    title: string;
  }) => (
    <h4>
      <span className="icon">
        <Icon />
      </span>
      {title}
    </h4>
  );

  // Helper function for updating form state
  const updateFormField = (
    field: keyof Omit<UserFormState, "isDirty">,
    value: any,
  ) => {
    setUserFormState((prevState) => ({
      ...prevState,
      [field]: value,
      isDirty: true,
    }));
  };

  // Helper function for resetting user form state
  const resetUserFormState = () => ({
    isAdmin: user.isAdmin,
    site: user.site,
    isDirty: false,
  });

  // Helper function for error handling
  const handleError = (
    error: unknown,
    fallbackMessage: string,
    operationType: string,
  ) => {
    if (error instanceof RokaApiError) {
      showToaster(`${fallbackMessage}: ${error.message}`, {
        type: "error",
        errorId: error.requestId,
      });
    } else {
      showToaster(`En uventet feil oppstod under ${operationType}`, {
        type: "error",
      });
      console.error(`Error ${operationType}:`, error);
    }
  };

  // Reset form state when user changes
  useEffect(() => {
    setUserFormState({
      isAdmin: user.isAdmin,
      site: user.site,
      isDirty: false,
    });
    setNewPassword("");
  }, [user]);

  const handleSaveUserChanges = async () => {
    try {
      await updateUser(user.username, {
        isAdmin: userFormState.isAdmin,
        isDisabled: user.isDisabled,
        site: userFormState.site,
      });
      showToaster(`Bruker "${user.username}" ble oppdatert!`, {
        type: "success",
      });

      // Update the user object and notify parent
      const updatedUser = {
        ...user,
        isAdmin: userFormState.isAdmin,
        site: userFormState.site,
      };
      onChange(updatedUser);

      setUserFormState((prevState) => ({
        ...prevState,
        isDirty: false,
      }));
    } catch (error) {
      handleError(
        error,
        "Kunne ikke oppdatere bruker",
        "oppdatering av bruker",
      );
    }
  };

  const handleDisableUser = async () => {
    try {
      await updateUser(user.username, {
        isAdmin: user.isAdmin,
        isDisabled: true,
        site: user.site,
      });
      showToaster(`Bruker "${user.username}" ble deaktivert.`, {
        type: "success",
      });

      // Update the user object and notify parent
      const updatedUser = {
        ...user,
        isDisabled: true,
      };
      onChange(updatedUser);
    } catch (error) {
      handleError(
        error,
        "Kunne ikke deaktivere bruker",
        "deaktivering av bruker",
      );
    }
  };

  const handleEnableUser = async () => {
    try {
      await updateUser(user.username, {
        isAdmin: user.isAdmin,
        isDisabled: false,
        site: user.site,
      });
      showToaster(`Bruker "${user.username}" ble re-aktivert.`, {
        type: "success",
      });

      // Update the user object and notify parent
      const updatedUser = {
        ...user,
        isDisabled: false,
      };
      onChange(updatedUser);
    } catch (error) {
      handleError(
        error,
        "Kunne ikke re-aktivere bruker",
        "re-aktivering av bruker",
      );
    }
  };

  const handleSetUserPassword = async () => {
    if (!newPassword) return;

    try {
      await updateUserPassword(user.username, newPassword);
      showToaster(`Passord for "${user.username}" ble oppdatert!`, {
        type: "success",
      });
      setNewPassword("");
    } catch (error) {
      handleError(
        error,
        "Kunne ikke oppdatere passord",
        "oppdatering av passord",
      );
    }
  };

  const handleResetForm = () => {
    setUserFormState(resetUserFormState());
  };

  return (
    <div className="expanded-section">
      <div className="horizontal-line" />

      <div className="impersonation">
        <SectionHeader icon={FaMask} title="Impersonéring" />
        <p>
          Som administrator kan du overta eller "impersonere" en annen bruker.
          Dette lar deg se og gjøre endringer på vegne av brukeren du
          impersonerer.
        </p>

        <Button
          onClick={() =>
            isCurrentlyImpersonated
              ? stopImpersonating()
              : startImpersonating(user)
          }
          buttonStyle="outline"
        >
          {isCurrentlyImpersonated
            ? "Stopp impersonering"
            : `Impersonér ${user.username}`}
        </Button>
      </div>

      <div className="horizontal-line" />

      <div className="form user-form">
        <SectionHeader icon={FaUser} title="Brukerinnstillinger" />
        <p>Her kan du endre innstillingene for brukeren.</p>
        <div className="form-group">
          <label>Rolle</label>
          <select
            value={userFormState.isAdmin ? "admin" : "user"}
            onChange={(e) =>
              updateFormField("isAdmin", e.target.value === "admin")
            }
          >
            <option value="user">Bruker</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-group">
          <label>Site</label>
          <input
            type="text"
            value={userFormState.site}
            onChange={(e) => updateFormField("site", e.target.value)}
          />
        </div>
        <div className="actions">
          <Button
            disabled={!userFormState.isDirty}
            className="reset-button"
            buttonStyle="outline"
            onClick={handleResetForm}
          >
            <RxReset />
          </Button>
          <Button
            className="save-button"
            disabled={!userFormState.isDirty}
            onClick={handleSaveUserChanges}
          >
            Lagre endringer
          </Button>
        </div>
      </div>

      <div className="horizontal-line" />

      <div className="form password-form">
        <SectionHeader icon={FaKey} title="Passord" />
        <p>Her kan du sette et nytt passord for brukeren.</p>
        <div className="form-group">
          <label></label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>
        <Button
          disabled={!newPassword}
          className="reset-password-button"
          onClick={handleSetUserPassword}
        >
          Sett nytt passord
        </Button>
      </div>

      <div className="horizontal-line" />

      <div className="form deactivation-form">
        <SectionHeader icon={TiCancel} title="Status" />
        <p>Her kan du deaktivere og re-aktivere brukeren.</p>
        {user.isDisabled ? (
          <Button
            onClick={handleEnableUser}
            className="activate-button"
            intent="success"
          >
            Re-aktiver bruker
          </Button>
        ) : (
          <Button
            onClick={handleDisableUser}
            className="deactivate-button"
            intent="danger"
          >
            Deaktiver bruker
          </Button>
        )}
      </div>
    </div>
  );
};

export default UserAdminForm;
